/**
 * Help assistant — strings.
 *
 * SAME PATTERN AS onboardingStrings.ts / incidentStrings.ts:
 *   HELP_STRINGS      English; also the inline `defaultValue` of every lookup,
 *                     so the panel renders correctly before the locale JSON is
 *                     updated.
 *   HELP_STRINGS_EL   Greek.
 * `registerHelpTranslations()` adds both to the running i18n instance without
 * touching i18n/index.ts or the locale JSON, so the parity guard — which reads
 * the JSON — is unaffected. Once merged into en.json/el.json these strings win
 * and registration becomes a harmless no-op (`addResources` never overwrites).
 *
 * ---------------------------------------------------------------------------
 *  REGISTER
 * ---------------------------------------------------------------------------
 * The same quiet register as onboarding: no exclamation marks, no celebration.
 * This is a person who is lost in a large compliance tool; the job is to orient
 * them and point at ONE next thing, calmly. The "where you are" copy describes
 * the screen in plain language and never editorialises.
 *
 * WHY THE PROSE IS HERE AND NOT DERIVED: the per-screen help is static text
 * about what a page is for — it is translator-owned wording, exactly like the
 * onboarding step titles. The *next step* itself is NOT here: that comes from
 * the onboarding derivation (see components/Help.tsx) and is rendered with the
 * onboarding string table, so there is one source of truth for it.
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const HELP_STRINGS = {
  // --- Trigger + panel chrome ----------------------------------------------
  'help.open': 'Help',
  'help.openAria': 'Open help and guidance',
  'help.title': 'Guide me',
  'help.subtitle':
    'Where you are, and the single next thing worth doing. This updates as your records change.',
  'help.close': 'Close',
  'help.loadFailed': 'Could not load your guidance.',
  'help.retry': 'Try again',

  // --- The single next step (chrome only; the step text is onboarding's) ----
  'help.next.title': 'Do this next',
  'help.next.allBlocked':
    'Everything left is waiting on an earlier step. Open the full checklist to see what.',
  'help.next.viewAll': 'Open the full checklist',
  'help.goal.title': 'Tell us what you are working towards',
  'help.goal.body':
    'The next step is chosen from your goal and your records. Pick a goal and the guidance falls into order.',
  'help.goal.choose': 'Choose a goal',
  'help.complete.title': 'Nothing outstanding',
  'help.complete.body':
    'Every step for your role is done. This stays here and will speak up again if a licence lapses or a new gap opens.',

  // --- Per-screen orientation ----------------------------------------------
  'help.here.title': 'On this screen',
  'help.here.doTitle': 'What you can do here',
  'help.manual.cta': 'Read the full guide for this screen',
  'help.manual.ctaGeneric': 'Open the manual',
  'help.links.title': 'Related screens',
  'help.roadmap.title': 'After that',
  'help.roadmap.none': 'Nothing further is queued right now.',

  // --- Per-page: a one-line "where you are" + up to three "you can" lines ---
  'help.page.dashboard.here':
    'Your organisation at a glance — compliance, open work and anything needing attention.',
  'help.page.dashboard.p1': 'See overall standing and what has changed recently.',
  'help.page.dashboard.p2': 'Jump to the single next action from the getting-started card.',

  'help.page.frameworks.here':
    'The regulations and standards you can work against. A licence opens a framework’s questionnaire.',
  'help.page.frameworks.p1': 'Licence a framework to unlock its assessment.',
  'help.page.frameworks.p2': 'Review what each framework requires before committing.',

  'help.page.catalogue.here':
    'The control library — one control can satisfy requirements across several frameworks.',
  'help.page.catalogue.p1': 'See which frameworks a control covers, and the evidence you already hold.',
  'help.page.catalogue.p2': 'Prove something once and reuse it wherever it applies.',

  'help.page.assessments.here':
    'Your questionnaires. Answering and finalising one is what generates your gap list.',
  'help.page.assessments.p1': 'Open an assessment to answer its questions.',
  'help.page.assessments.p2': 'Finalise a completed assessment to produce gaps.',

  'help.page.assessmentDetail.here':
    'A single assessment. Answer each question, then finalise when you are ready.',
  'help.page.assessmentDetail.p1': 'Record an answer and attach evidence to back it up.',
  'help.page.assessmentDetail.p2': 'Finalise to lock the result and generate your gaps.',

  'help.page.gaps.here':
    'The shortfalls found by your assessments. Each one can carry a remediation plan.',
  'help.page.gaps.p1': 'Put a plan against a gap so someone owns it.',
  'help.page.gaps.p2': 'Filter to gaps with no plan — those are the ones that linger.',

  'help.page.plans.here':
    'Remediation plans — who is doing what, by when, to close your gaps.',
  'help.page.plans.p1': 'Track progress and ownership of each plan.',
  'help.page.plans.p2': 'A gap closes once every plan against it is done.',

  'help.page.gettingStarted.here':
    'The full, goal-driven checklist. Every tick is read from your records, not stored.',
  'help.page.gettingStarted.p1': 'Change what your organisation is working towards.',
  'help.page.gettingStarted.p2': 'See every step, its state and where it leads.',

  'help.page.incidents.here':
    'Where a security incident is recorded and its reporting clock is tracked.',
  'help.page.incidents.p1': 'Log an incident and work through its notification deadlines.',
  'help.page.incidents.p2': 'NIS2 Article 23 allows 24 hours for an early warning.',

  'help.page.policies.here':
    'Your documented policies — asked for by name in NIS2 Article 21 and ISO 27001 Annex A.',
  'help.page.policies.p1': 'Generate a policy from a template rather than a blank page.',
  'help.page.policies.p2': 'Keep policies active and up to date for your auditor.',

  'help.page.vendors.here':
    'The suppliers you depend on. You cannot assess one the product has not been told about.',
  'help.page.vendors.p1': 'Add a supplier to your register.',
  'help.page.vendors.p2': 'Record how much exposure each one carries.',

  'help.page.vendorAssessments.here':
    'Questionnaires sent to suppliers — what you asked, what came back, what expired.',
  'help.page.vendorAssessments.p1': 'Send a questionnaire; suppliers answer by link, with no account.',
  'help.page.vendorAssessments.p2': 'Chase anything still outstanding.',

  'help.page.organizations.here':
    'The client organisations you manage. Most work happens inside a client’s tenancy, not yours.',
  'help.page.organizations.p1': 'Add a client organisation.',
  'help.page.organizations.p2': 'Secure each client’s consent before reading their data.',

  'help.page.users.here':
    'The people in this organisation. Plans need owners and policies need approvers.',
  'help.page.users.p1': 'Invite a colleague and set their role.',
  'help.page.users.p2': 'Roles decide what each person may see and do.',

  'help.page.reports.here':
    'Exports for your auditor — the Statement of Applicability and assessment reports download here.',
  'help.page.reports.p1': 'Generate a report for a finalised assessment.',
  'help.page.reports.p2': 'Hand an auditor a document rather than a screenshot.',

  'help.page.audit.here':
    'The trail of who changed what, and when. Read-only, and kept for accountability.',
  'help.page.audit.p1': 'Trace a change back to the person and the moment it happened.',

  'help.page.security.here':
    'Your account security — sign-in and your second factor.',
  'help.page.security.p1': 'Move from emailed codes to an authenticator app.',
  'help.page.security.p2': 'An authenticator still works when email does not.',

  'help.page.news.here': 'Updates relevant to your compliance programme.',
  'help.page.news.p1': 'Catch up on what has changed.',

  'help.page.translations.here':
    'Manage the wording users see, in each language you support.',
  'help.page.translations.p1': 'Edit or add translations for your organisation.',

  'help.page.contentWatch.here':
    'Review changes to framework content before they reach your users.',
  'help.page.contentWatch.p1': 'Approve or hold proposed content updates.',

  'help.page.soa.here':
    'Your Statement of Applicability — ISO 27001 clause 6.1.3(d), the document an auditor asks for first.',
  'help.page.soa.p1': 'Write your ISMS scope statement.',
  'help.page.soa.p2': 'Decide and justify every Annex A control — a blank justification is the commonest finding.',

  'help.page.trustCentre.here':
    'A page you can send instead of filling in the next security questionnaire by hand.',
  'help.page.trustCentre.p1': 'Choose exactly what it shows, then publish it.',
  'help.page.trustCentre.p2': 'It stays current on its own as your records change.',

  'help.page.scans.here':
    'Passive external checks on a domain — certificates, headers, DNS. These cost credits.',
  'help.page.scans.p1': 'Run a check on a domain that matters.',
  'help.page.scans.p2': 'Read the report once it is ready.',

  'help.page.credits.here':
    'Your credit balance and ledger. Licences, questionnaires and scans are paid in credits.',
  'help.page.credits.p1': 'See what has been spent and on what.',
  'help.page.credits.p2': 'Allocate credits to a client so they can start.',

  'help.page.generic.here':
    'You are inside OpenComply. Use the next step below to stay on track, or open the full checklist.',
  'help.page.generic.p1': 'Every screen is reachable from the navigation.',
} as const;

export type HelpKey = keyof typeof HELP_STRINGS;

export const HELP_STRINGS_EL: Record<HelpKey, string> = {
  'help.open': 'Βοήθεια',
  'help.openAria': 'Άνοιγμα βοήθειας και καθοδήγησης',
  'help.title': 'Καθοδήγησέ με',
  'help.subtitle':
    'Πού βρίσκεστε και ποιο είναι το μοναδικό επόμενο βήμα που αξίζει. Ενημερώνεται καθώς αλλάζουν τα δεδομένα σας.',
  'help.close': 'Κλείσιμο',
  'help.loadFailed': 'Δεν ήταν δυνατή η φόρτωση της καθοδήγησης.',
  'help.retry': 'Δοκιμάστε ξανά',

  'help.next.title': 'Το επόμενο βήμα',
  'help.next.allBlocked':
    'Ό,τι απομένει περιμένει ένα προηγούμενο βήμα. Ανοίξτε τη λίστα για να δείτε ποιο.',
  'help.next.viewAll': 'Ανοίξτε όλη τη λίστα',
  'help.goal.title': 'Πείτε μας τι επιδιώκετε',
  'help.goal.body':
    'Το επόμενο βήμα επιλέγεται από τον στόχο και τα δεδομένα σας. Διαλέξτε στόχο και η καθοδήγηση μπαίνει σε σειρά.',
  'help.goal.choose': 'Διαλέξτε στόχο',
  'help.complete.title': 'Δεν εκκρεμεί κάτι',
  'help.complete.body':
    'Κάθε βήμα για τον ρόλο σας έχει ολοκληρωθεί. Παραμένει εδώ και θα σας ειδοποιήσει αν λήξει μια άδεια ή ανοίξει νέο κενό.',

  'help.here.title': 'Σε αυτή την οθόνη',
  'help.here.doTitle': 'Τι μπορείτε να κάνετε εδώ',
  'help.manual.cta': 'Διαβάστε τον πλήρη οδηγό αυτής της οθόνης',
  'help.manual.ctaGeneric': 'Άνοιγμα του εγχειριδίου',
  'help.links.title': 'Σχετικές οθόνες',
  'help.roadmap.title': 'Μετά από αυτό',
  'help.roadmap.none': 'Δεν υπάρχει κάτι άλλο σε σειρά αυτή τη στιγμή.',

  'help.page.dashboard.here':
    'Ο οργανισμός σας με μια ματιά — συμμόρφωση, ανοιχτές εργασίες και ό,τι χρειάζεται προσοχή.',
  'help.page.dashboard.p1': 'Δείτε τη συνολική εικόνα και τι άλλαξε πρόσφατα.',
  'help.page.dashboard.p2': 'Μεταβείτε στο μοναδικό επόμενο βήμα από την κάρτα πρώτων βημάτων.',

  'help.page.frameworks.here':
    'Τα πλαίσια και πρότυπα με τα οποία μπορείτε να δουλέψετε. Η άδεια ξεκλειδώνει το ερωτηματολόγιο ενός πλαισίου.',
  'help.page.frameworks.p1': 'Αποκτήστε άδεια για ένα πλαίσιο ώστε να ανοίξει η αξιολόγησή του.',
  'help.page.frameworks.p2': 'Δείτε τι απαιτεί κάθε πλαίσιο πριν δεσμευτείτε.',

  'help.page.catalogue.here':
    'Η βιβλιοθήκη δικλίδων — μία δικλίδα μπορεί να καλύπτει απαιτήσεις πολλών πλαισίων.',
  'help.page.catalogue.p1': 'Δείτε ποια πλαίσια καλύπτει μια δικλίδα και τι τεκμήρια έχετε ήδη.',
  'help.page.catalogue.p2': 'Αποδείξτε κάτι μία φορά και χρησιμοποιήστε το όπου ισχύει.',

  'help.page.assessments.here':
    'Τα ερωτηματολόγιά σας. Η απάντηση και η οριστικοποίηση ενός παράγει τη λίστα κενών σας.',
  'help.page.assessments.p1': 'Ανοίξτε μια αξιολόγηση για να απαντήσετε τις ερωτήσεις της.',
  'help.page.assessments.p2': 'Οριστικοποιήστε μια ολοκληρωμένη αξιολόγηση για να παραχθούν κενά.',

  'help.page.assessmentDetail.here':
    'Μία αξιολόγηση. Απαντήστε κάθε ερώτηση και οριστικοποιήστε όταν είστε έτοιμοι.',
  'help.page.assessmentDetail.p1': 'Καταγράψτε μια απάντηση και επισυνάψτε τεκμήριο.',
  'help.page.assessmentDetail.p2': 'Οριστικοποιήστε για να κλειδώσει το αποτέλεσμα και να παραχθούν τα κενά.',

  'help.page.gaps.here':
    'Τα κενά που εντόπισαν οι αξιολογήσεις σας. Κάθε ένα μπορεί να έχει σχέδιο αποκατάστασης.',
  'help.page.gaps.p1': 'Βάλτε σχέδιο σε ένα κενό ώστε κάποιος να το αναλάβει.',
  'help.page.gaps.p2': 'Φιλτράρετε στα κενά χωρίς σχέδιο — αυτά είναι που μένουν.',

  'help.page.plans.here':
    'Σχέδια αποκατάστασης — ποιος κάνει τι, ως πότε, για να κλείσουν τα κενά σας.',
  'help.page.plans.p1': 'Παρακολουθήστε την πρόοδο και την ευθύνη κάθε σχεδίου.',
  'help.page.plans.p2': 'Ένα κενό κλείνει όταν ολοκληρωθεί κάθε σχέδιο που το αφορά.',

  'help.page.gettingStarted.here':
    'Η πλήρης λίστα βάσει στόχου. Κάθε βήμα υπολογίζεται από τα δεδομένα σας, δεν αποθηκεύεται.',
  'help.page.gettingStarted.p1': 'Αλλάξτε τι επιδιώκει ο οργανισμός σας.',
  'help.page.gettingStarted.p2': 'Δείτε κάθε βήμα, την κατάστασή του και πού οδηγεί.',

  'help.page.incidents.here':
    'Εδώ καταγράφεται ένα περιστατικό ασφάλειας και παρακολουθείται το χρονόμετρο αναφοράς του.',
  'help.page.incidents.p1': 'Καταγράψτε περιστατικό και δουλέψτε τις προθεσμίες ειδοποίησης.',
  'help.page.incidents.p2': 'Το άρθρο 23 της NIS2 δίνει 24 ώρες για πρώιμη προειδοποίηση.',

  'help.page.policies.here':
    'Οι τεκμηριωμένες πολιτικές σας — ζητούνται ρητά στο άρθρο 21 της NIS2 και στο Παράρτημα A του ISO 27001.',
  'help.page.policies.p1': 'Δημιουργήστε πολιτική από πρότυπο, όχι από λευκή σελίδα.',
  'help.page.policies.p2': 'Κρατήστε τις πολιτικές ενεργές και ενημερωμένες για τον ελεγκτή.',

  'help.page.vendors.here':
    'Οι προμηθευτές από τους οποίους εξαρτάστε. Δεν αξιολογείτε κάποιον που το σύστημα δεν γνωρίζει.',
  'help.page.vendors.p1': 'Προσθέστε έναν προμηθευτή στο μητρώο σας.',
  'help.page.vendors.p2': 'Καταγράψτε πόση έκθεση φέρνει ο καθένας.',

  'help.page.vendorAssessments.here':
    'Ερωτηματολόγια προς προμηθευτές — τι ζητήσατε, τι επιστράφηκε, τι έληξε.',
  'help.page.vendorAssessments.p1': 'Στείλτε ερωτηματολόγιο· απαντούν μέσω συνδέσμου, χωρίς λογαριασμό.',
  'help.page.vendorAssessments.p2': 'Υπενθυμίστε ό,τι εκκρεμεί.',

  'help.page.organizations.here':
    'Οι οργανισμοί-πελάτες που διαχειρίζεστε. Το έργο γίνεται μέσα στον πελάτη, όχι σε εσάς.',
  'help.page.organizations.p1': 'Προσθέστε έναν οργανισμό-πελάτη.',
  'help.page.organizations.p2': 'Εξασφαλίστε τη συγκατάθεση κάθε πελάτη πριν δείτε τα δεδομένα του.',

  'help.page.users.here':
    'Οι άνθρωποι σε αυτόν τον οργανισμό. Τα σχέδια θέλουν υπεύθυνους και οι πολιτικές εγκρίνοντες.',
  'help.page.users.p1': 'Προσκαλέστε συνάδελφο και ορίστε τον ρόλο του.',
  'help.page.users.p2': 'Οι ρόλοι καθορίζουν τι βλέπει και κάνει ο καθένας.',

  'help.page.reports.here':
    'Εξαγωγές για τον ελεγκτή σας — το Πεδίο Εφαρμογής και οι αναφορές αξιολόγησης κατεβαίνουν εδώ.',
  'help.page.reports.p1': 'Δημιουργήστε αναφορά για μια οριστικοποιημένη αξιολόγηση.',
  'help.page.reports.p2': 'Δώστε στον ελεγκτή έγγραφο αντί για στιγμιότυπο οθόνης.',

  'help.page.audit.here':
    'Το ιστορικό του ποιος άλλαξε τι και πότε. Μόνο για ανάγνωση, για λόγους λογοδοσίας.',
  'help.page.audit.p1': 'Εντοπίστε μια αλλαγή πίσω στο πρόσωπο και τη στιγμή της.',

  'help.page.security.here':
    'Η ασφάλεια του λογαριασμού σας — σύνδεση και δεύτερος παράγοντας.',
  'help.page.security.p1': 'Περάστε από κωδικούς email σε εφαρμογή αυθεντικοποίησης.',
  'help.page.security.p2': 'Η εφαρμογή αυθεντικοποίησης δουλεύει ακόμα κι όταν το email δεν δουλεύει.',

  'help.page.news.here': 'Ενημερώσεις σχετικές με το πρόγραμμα συμμόρφωσής σας.',
  'help.page.news.p1': 'Δείτε τι άλλαξε.',

  'help.page.translations.here':
    'Διαχειριστείτε το κείμενο που βλέπουν οι χρήστες, σε κάθε γλώσσα που υποστηρίζετε.',
  'help.page.translations.p1': 'Επεξεργαστείτε ή προσθέστε μεταφράσεις για τον οργανισμό σας.',

  'help.page.contentWatch.here':
    'Ελέγξτε αλλαγές στο περιεχόμενο των πλαισίων πριν φτάσουν στους χρήστες σας.',
  'help.page.contentWatch.p1': 'Εγκρίνετε ή αναστείλετε προτεινόμενες ενημερώσεις περιεχομένου.',

  'help.page.soa.here':
    'Το Πεδίο Εφαρμογής σας — ρήτρα 6.1.3(d) του ISO 27001, το πρώτο έγγραφο που ζητά ο ελεγκτής.',
  'help.page.soa.p1': 'Γράψτε το πεδίο εφαρμογής του ΣΔΑΠ.',
  'help.page.soa.p2': 'Αποφασίστε και αιτιολογήστε κάθε δικλίδα του Παραρτήματος A — η κενή αιτιολόγηση είναι το συχνότερο εύρημα.',

  'help.page.trustCentre.here':
    'Μια σελίδα που στέλνετε αντί να συμπληρώνετε το επόμενο ερωτηματολόγιο στο χέρι.',
  'help.page.trustCentre.p1': 'Επιλέξτε τι δείχνει και δημοσιεύστε το.',
  'help.page.trustCentre.p2': 'Μένει ενημερωμένο μόνο του καθώς αλλάζουν τα δεδομένα σας.',

  'help.page.scans.here':
    'Παθητικοί εξωτερικοί έλεγχοι σε ένα χωρίο — πιστοποιητικά, κεφαλίδες, DNS. Κοστίζουν μονάδες.',
  'help.page.scans.p1': 'Εκτελέστε έλεγχο σε ένα χωρίο που μετράει.',
  'help.page.scans.p2': 'Διαβάστε την αναφορά όταν είναι έτοιμη.',

  'help.page.credits.here':
    'Το υπόλοιπο και το ιστορικό μονάδων σας. Άδειες, ερωτηματολόγια και έλεγχοι πληρώνονται σε μονάδες.',
  'help.page.credits.p1': 'Δείτε τι ξοδεύτηκε και σε τι.',
  'help.page.credits.p2': 'Κατανείμετε μονάδες σε έναν πελάτη ώστε να ξεκινήσει.',

  'help.page.generic.here':
    'Βρίσκεστε μέσα στο OpenComply. Χρησιμοποιήστε το επόμενο βήμα παρακάτω ή ανοίξτε όλη τη λίστα.',
  'help.page.generic.p1': 'Κάθε οθόνη είναι προσβάσιμη από την πλοήγηση.',
};

let registered = false;

/**
 * Register both tables with the running i18n instance.
 *
 * Idempotent, and `addResources` never overwrites an existing key — so once
 * these land in en.json and el.json the bundled strings win and this becomes a
 * no-op rather than a second source of truth.
 */
export function registerHelpTranslations(): void {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [
    ['en', HELP_STRINGS],
    ['el', HELP_STRINGS_EL],
  ] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerHelpTranslations();

/** Lookup helper with the English table as the inline fallback. */
export function useHelpT() {
  const { t } = useTranslation();

  const hx = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      t(key, {
        defaultValue: HELP_STRINGS[key as HelpKey] ?? key,
        ...options,
      }) as string,
    [t],
  );

  return { hx };
}
